import type { GetProductSearchParams } from "@zaprooecommerce/magento-types";
import { hash } from "ohash";
import type { ProductShort } from "~/types/product";
import type { Aggregation } from "~/types/catalog/Aggregation";
import type { CatalogSearchResponse } from "~/types/catalog/CatalogSearchResponse";

export function useProductsFetcherData(
  params: MaybeRef<GetProductSearchParams>,
  options?: {
    key?: string;
    query?: string;
    projection?: string;
  },
) {
  const { products: fetchProducts } = useMagentoSdk();

  const query = options?.query ?? "catalogProducts";
  const projection = options?.projection ?? "ProductShortProj";

  const key =
    options?.key ?? `magento-pfd-${hash(JSON.stringify(unref(params)))}`;

  const asyncData = useSyncCachedAsyncData(key, async () => {
    const res = await fetchProducts(unref(params), {
      customQuery: {
        products: query,
        metadata: {
          projection,
        },
      },
    });

    throwGqlError(res);

    return res.data.products as any as CatalogSearchResponse<ProductShort>;
  });

  const { data, pending, refresh, error, status } = asyncData;

  const products = computed<ProductShort[]>(() => data.value?.items ?? []);

  const aggregations = computed<Aggregation[]>(
    () => (data.value?.aggregations as Aggregation[]) ?? [],
  );

  const pageInfo = computed(() => data.value?.page_info ?? undefined);

  const totalCount = computed(() => data.value?.total_count ?? 0);

  return {
    asyncData,
    data,
    pending,
    refresh,
    error,
    status,
    products,
    aggregations,
    pageInfo,
    totalCount,
  };
}
